import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { FaMicrosoft } from 'react-icons/fa';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleMicrosoftLogin = async () => {
    console.log('[Login] redirection vers Keycloak…');
    setLoading(true);
    try {
      await login();
      // With loginRedirect, this line should never be reached
      // (la page navigue vers Keycloak, puis Microsoft)
      console.log('[Login] retour inattendu : le flux par redirection ne revient pas ici');
    } catch (error) {
      console.error('[Login] Error:', error);
      const msg = error?.message || 'Impossible de joindre Keycloak';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 login-bg relative">
        <div className="login-overlay absolute inset-0 flex flex-col justify-center items-center text-white p-12">
          <div className="max-w-md">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-sm bg-white/20 backdrop-blur flex items-center justify-center">
                <FaMicrosoft className="h-6 w-6" />
              </div>
              <span className="font-heading font-extrabold text-3xl">DevOps Flow</span>
            </div>
            <h1 className="text-4xl font-heading font-extrabold mb-4">
              Simplifiez votre gestion Azure DevOps
            </h1>
            <p className="text-lg text-white/80 leading-relaxed">
              Créez des tâches, suivez le temps, liez vos commits et exportez vos rapports
              en toute simplicité.
            </p>
            <div className="mt-8 flex gap-4">
              <div className="flex items-center gap-2 text-sm text-white/70">
                <div className="w-2 h-2 rounded-full bg-green-400"></div>
                Multi-projets
              </div>
              <div className="flex items-center gap-2 text-sm text-white/70">
                <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                Time tracking
              </div>
              <div className="flex items-center gap-2 text-sm text-white/70">
                <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                Exports PDF/Excel
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-sm bg-primary flex items-center justify-center">
              <FaMicrosoft className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-extrabold text-2xl">DevOps Flow</span>
          </div>

          <Card className="border-border">
            <CardHeader className="space-y-1 text-center">
              <CardTitle className="text-2xl font-heading">Bienvenue</CardTitle>
              <CardDescription>
                Connectez-vous avec votre compte professionnel ELIKA
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Button
                className="w-full rounded-sm h-12 text-base"
                onClick={handleMicrosoftLogin}
                disabled={loading}
                data-testid="microsoft-login-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <FaMicrosoft className="mr-3 h-5 w-5" />
                    Se connecter
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                En vous connectant, vous acceptez que votre nom et email Microsoft
                soient utilisés pour créer votre compte DevOps Flow.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
